import {
  AdviceProductDetailsFormValues,
  EconomicalHealthWish,
} from "Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import {
  AdviceCoverageName,
  AdvicePriceCalculationResult,
  AdviceProductChangeType,
  InsuranceAdvice,
  PurchaseRecommendation,
  RecommendationReason,
  SinglePayment,
  UserAuthorizationLevel,
  YearlyPayment,
  YearlyPaymentDetails,
} from "Store/advice/interfaces";
import {
  AkaCriticalIllness,
  CriticalIllnessCoverage,
  DeathCoverage,
  DisabilityCoverage,
} from "Store/interfaces/products/coverage";
import {
  AkaCriticalIllnessInsuranceType,
  CriticalIllnessInsuranceType,
  DeathInsuranceType,
  DisabilityInsuranceType,
} from "../../adviceTypes";
import {
  AdvicePriceFormValues,
  OneTimePaymentAdvisorChoice,
} from "./interfaces";

import { VALUE_TYPE } from "Components/NumberValue";
import { formatToLocaleString } from "Formatting/formatNumber";
import {
  formatNumberStringToNumber,
  getPriceFormatFromValue,
} from "Formatting/index";
import { getCorrectPayment } from "Store/advice/utils";
import {
  CoverageNeed,
  PaymentConfiguration,
  ProductCoverageName,
  PurchaseRestriction,
  PurchaseRestrictionReason,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPayment,
  getPaymentRestriction,
  getRecommended,
  isAllowedAmount,
  isRecommended,
} from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { Partner } from "Store/partners/partnerInterfaces";
import { TextLanguage } from "Store/texts/interfaces";
import { isDefined } from "Utils/functions";
import { replacePlaceholder } from "Utils/replacePlaceholder";
import { hasDisabilityCapitalCustomerOptions } from "../../ProductPriceAka/AkaAdviceCards/DisabilityAdviceCard/Forms/DisabilityOneTimePayment/utils";

export const getMonthlyPrice = (
  insurancePrice: AdvicePriceCalculationResult,
): number => Number(insurancePrice.prices[0].monthlyPricePrecise);

export const getPurchaseFromAdvisorCardMessage = (
  partnerId: string,
  insuranceAdvice: InsuranceAdvice,
  purchaseFromAdvisor: any,
  lang: string,
): string => {
  const adviceValue = getCorrectPaymentValue(
    UserAuthorizationLevel.PRODUCT_MANAGER,
    insuranceAdvice.singlePayment,
  );
  const isMaximumPayout =
    adviceValue === getAdviceRestrictions(insuranceAdvice).maxProductManager;

  const oneTimePayment: string = purchaseFromAdvisor.payment.oneTime[
    lang
  ].replace(
    "{{value}}",
    `${formatToLocaleString(adviceValue ?? 0)} ${VALUE_TYPE.NOK}`,
  );
  const weAdvice: string = purchaseFromAdvisor.weAdvice[lang]
    .replace("{{payment}}", oneTimePayment)
    .replace(
      "{{isMaximumPayout}}",
      isMaximumPayout ? purchaseFromAdvisor.isMaximumPayout[lang] : "",
    );
  return replaceUserSupportNumberInText(
    partnerId,
    purchaseFromAdvisor.userSupportNumber,
    weAdvice,
    lang,
  );
};

export const getPurchaseFromAdvisorMessage = (
  isAdvisor: boolean,
  partnerId: string,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  purchaseFromAdvisor: any,
  lang: string,
): string => {
  const { adviceValue } = getCorrectAkaAdviceValues(
    need,
    productCoverageName,
    isAdvisor,
    UserAuthorizationLevel.PRODUCT_MANAGER,
  );
  const paymentRestriction = getPaymentRestriction(need, productCoverageName);
  const isMaximumPayout =
    adviceValue === paymentRestriction.maxPayouts.PRODUCT_MANAGER;

  const oneTimePayment: string = purchaseFromAdvisor.payment.oneTime[
    lang
  ].replace(
    "{{value}}",
    `${formatToLocaleString(adviceValue ?? 0)} ${VALUE_TYPE.NOK}`,
  );
  const weAdvice: string = purchaseFromAdvisor.weAdvice[lang]
    .replace("{{payment}}", oneTimePayment)
    .replace(
      "{{isMaximumPayout}}",
      isMaximumPayout ? purchaseFromAdvisor.isMaximumPayout[lang] : "",
    );
  return replaceUserSupportNumberInText(
    partnerId,
    purchaseFromAdvisor.userSupportNumber,
    weAdvice,
    lang,
  );
};

export const replaceUserSupportNumberInText = (
  partnerId: string,
  userSupportNumbers: any,
  textToReplaceIn: string,
  lang: string,
): string => {
  const userSupportNumber =
    userSupportNumbers[
      partnerId === Partner.DANSKE_BANK ? "danskebank" : "storebrand"
    ][lang];
  return textToReplaceIn.replace("{{userSupportNumber}}", userSupportNumber);
};

export const isOneTimePaymentAllowed = (
  singlePayment: SinglePayment | PaymentConfiguration,
): boolean => {
  if ("recommendationConfiguration" in singlePayment) {
    const recommendation =
      singlePayment.recommendationConfiguration.recommendation;
    return (
      recommendation === PurchaseRecommendation.PURCHASE ||
      recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR ||
      recommendation === PurchaseRecommendation.NOT_RECOMMENDED
    );
  }
  return (
    singlePayment.restrictions.purchaseRestriction ===
    PurchaseRestriction.ALLOWED
  );
};

export const getEconomicalHealthWishText = (
  adviceProductIntroFormValues: AdviceProductDetailsFormValues,
  lang: string,
  formText: any,
): string => {
  switch (adviceProductIntroFormValues.economicalHealthWish) {
    case EconomicalHealthWish.FINANCES_LIKE_TODAY:
      return formText.aboutYou.economicalHealthWish.options.financesLikeToday[
        lang
      ];
    case EconomicalHealthWish.SLIGHT_ECONOMIC_DOWNTURN:
      return formText.aboutYou.economicalHealthWish.options
        .slightEconomicDownturn[lang];
    case EconomicalHealthWish.SIGNIFICANT_ECONOMIC_DOWNTURN:
    default:
      return formText.aboutYou.economicalHealthWish.options
        .significantEconomicDownturn[lang];
  }
};

export const getCorrectPaymentValue = (
  authorization: UserAuthorizationLevel,
  payment?: YearlyPayment | SinglePayment,
): number => {
  let paymentValue = payment?.advice?.recommended;
  if (payment?.restrictions) {
    const maxPayoutForAuthorization =
      payment.restrictions.maxPayouts[authorization];
    if (paymentValue && maxPayoutForAuthorization < paymentValue) {
      paymentValue = maxPayoutForAuthorization;
    }
  }
  return paymentValue ?? 0;
};

interface DisabilityPlotValues {
  netValue: number;
  monthlyIncome: number;
  monthlyNetNavPayout: number;
  monthlyNetEmployerPaidDisabilityPension: number;
}
export const getDisabilityPlotValues = (
  currentSliderValue: number,
  details?: YearlyPaymentDetails,
): DisabilityPlotValues => {
  if (!details) {
    return {
      netValue: 0,
      monthlyIncome: 0,
      monthlyNetNavPayout: 0,
      monthlyNetEmployerPaidDisabilityPension: 0,
    };
  }
  return {
    netValue: roundToNearestHundred(
      (currentSliderValue * (1 - details.yearlyPaymentTaxRate)) / 12,
    ),
    monthlyIncome: roundToNearestHundred(details.netIncome / 12),
    monthlyNetNavPayout: roundToNearestHundred(details.netNavPayout / 12),
    monthlyNetEmployerPaidDisabilityPension: roundToNearestHundred(
      details.netEmployerPaidDisabilityPension / 12,
    ),
  };
};

export const roundToNearestHundred = (value: number): number =>
  Math.round(value / 100) * 100;

export type AdviceInsuranceType =
  | AkaCriticalIllnessInsuranceType
  | DeathInsuranceType
  | CriticalIllnessInsuranceType
  | DisabilityInsuranceType;

export const getAdviceInsuranceType = (
  coverageGroup: AdviceCoverageName,
  isAdvisor: boolean,
  disabilityOneTimePayment?: SinglePayment | PaymentConfiguration,
): AdviceInsuranceType => {
  switch (coverageGroup) {
    case AkaCriticalIllness.AKA_CRITICAL_ILLNESS:
      return AkaCriticalIllnessInsuranceType.AKA_CRITICAL_ILLNESS;
    case DeathCoverage.DEATH_NOT_SMOKER:
      return DeathInsuranceType.DEATH_NOT_SMOKER;
    case CriticalIllnessCoverage.CRITICAL_ILLNESS_NOT_SMOKER:
      return CriticalIllnessInsuranceType.CRITICAL_ILLNESS_NOT_SMOKER;
    case DisabilityCoverage.DISABILITY_FLEX:
    default:
      if (isDefined(disabilityOneTimePayment)) {
        return getCorrectInsuranceTypeForDisabilityOneTimePayment(
          isAdvisor,
          disabilityOneTimePayment,
        );
      }
      return DisabilityInsuranceType.DISABILITY_FLEX;
  }
};

export const getAdvicePlotRange = (
  isAdvisor: boolean,
  debtOrIncome: number,
  sliderMax: number,
): number[] | undefined => {
  if (isAdvisor) {
    return undefined;
  }
  return debtOrIncome > sliderMax ? [0, debtOrIncome] : [0, sliderMax];
};

// Used by old advice
export const getOneTimePaymentTextDeprecated = (
  adviceProductIntroFormValues: AdviceProductDetailsFormValues,
  oneTimePaymentValue: number,
  oneTimePayment: any,
  lang: string,
): string => {
  let oneTimePaymentText = `${
    oneTimePayment.adviceText.part1[lang]
  } ${formatToLocaleString(oneTimePaymentValue)} `;
  if (
    adviceProductIntroFormValues.economicalHealthWish ===
    EconomicalHealthWish.SLIGHT_ECONOMIC_DOWNTURN
  ) {
    oneTimePaymentText = `${oneTimePaymentText}${oneTimePayment.adviceText.part2[lang]} ${oneTimePayment.adviceText.reasonSlightEconomicDownturn[lang]}`;
  } else if (
    adviceProductIntroFormValues.economicalHealthWish ===
    EconomicalHealthWish.FINANCES_LIKE_TODAY
  ) {
    oneTimePaymentText = `${oneTimePaymentText}${oneTimePayment.adviceText.part2[lang]} ${oneTimePayment.adviceText.reasonFinancesLikeToday[lang]}`;
  } else {
    oneTimePaymentText = oneTimePaymentText + `${VALUE_TYPE.NOK}.`;
  }
  return oneTimePaymentText;
};

export const getOneTimePaymentText = (
  need: CoverageNeed,
  adviceProductIntroFormValues: AdviceProductDetailsFormValues,
  oneTimePaymentValue: number,
  oneTimePayment: any,
  lang: string,
): string => {
  let oneTimePaymentText = `${
    oneTimePayment.adviceText.part1[lang]
  } ${formatToLocaleString(oneTimePaymentValue)} `;

  if (!isRecommended(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL)) {
    return oneTimePaymentText + `${VALUE_TYPE.NOK}.`;
  }

  if (
    adviceProductIntroFormValues.economicalHealthWish ===
    EconomicalHealthWish.SLIGHT_ECONOMIC_DOWNTURN
  ) {
    oneTimePaymentText = `${oneTimePaymentText}${oneTimePayment.adviceText.part2[lang]} ${oneTimePayment.adviceText.reasonSlightEconomicDownturn[lang]}`;
  } else if (
    adviceProductIntroFormValues.economicalHealthWish ===
    EconomicalHealthWish.FINANCES_LIKE_TODAY
  ) {
    oneTimePaymentText = `${oneTimePaymentText}${oneTimePayment.adviceText.part2[lang]} ${oneTimePayment.adviceText.reasonFinancesLikeToday[lang]}`;
  } else {
    oneTimePaymentText = oneTimePaymentText + `${VALUE_TYPE.NOK}.`;
  }
  return oneTimePaymentText;
};

export const getOneTimePaymentTextWithoutValue = (
  need: CoverageNeed,
  adviceProductIntroFormValues: AdviceProductDetailsFormValues,
  oneTimePayment: any,
  lang: string,
): string => {
  if (!isRecommended(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL)) {
    return oneTimePayment.adviceText.addPayment[lang];
  }

  switch (adviceProductIntroFormValues.economicalHealthWish) {
    case EconomicalHealthWish.SIGNIFICANT_ECONOMIC_DOWNTURN:
      return oneTimePayment.adviceText.addPayment[lang];
    case EconomicalHealthWish.SLIGHT_ECONOMIC_DOWNTURN:
      return oneTimePayment.adviceText.addPaymentSlightDownturn[lang];
    case EconomicalHealthWish.FINANCES_LIKE_TODAY:
      return oneTimePayment.adviceText.addPaymentLikeToday[lang];
    default:
      throw new Error("Invalid economical health wish!");
  }
};

export const getOneTimePaymentCheckboxText = (
  need: CoverageNeed,
  adviceProductIntroFormValues: AdviceProductDetailsFormValues,
  oneTimePayment: any,
  lang: string,
): string => {
  const hasOptions = hasDisabilityCapitalCustomerOptions(need);
  const {
    advice: { recommended },
    restrictions,
  } = getPayment(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL);
  if (
    isAllowedAmount(
      recommended,
      need,
      ProductCoverageName.DISABILITY_FLEX_CAPITAL,
      false,
    )
  ) {
    return getOneTimePaymentText(
      need,
      adviceProductIntroFormValues,
      recommended,
      oneTimePayment,
      lang,
    );
  }
  if (!hasOptions) {
    return getOneTimePaymentText(
      need,
      adviceProductIntroFormValues,
      restrictions.paymentRestriction.maxPayouts[
        UserAuthorizationLevel.CUSTOMER
      ],
      oneTimePayment,
      lang,
    );
  }
  return getOneTimePaymentTextWithoutValue(
    need,
    adviceProductIntroFormValues,
    oneTimePayment,
    lang,
  );
};

export const getOneTimePaymentRadioHint = (
  need: CoverageNeed,
  adviceCard: any,
  lang: TextLanguage,
): string | undefined => {
  const recommended = getRecommended(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const restrictions = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const customerMax = restrictions.maxPayouts[UserAuthorizationLevel.CUSTOMER];

  const recommendedIsMax = recommended === customerMax;
  if (recommended > 0) {
    return replacePlaceholder(
      adviceCard.disabilityOneTimePayment.options.hint[lang],
      {
        amount: getPriceFormatFromValue(recommended, VALUE_TYPE.NOK),
        higherOrLower: recommendedIsMax
          ? adviceCard.disabilityOneTimePayment.options.lowerVariable[lang]
          : adviceCard.disabilityOneTimePayment.options.higherVariable[lang],
      },
    );
  }
  return undefined;
};

interface ReturnData {
  finalDisabilityOneTimePaymentSet: boolean;
  coverageAmountCapital: number;
  disabilityOneTimePaymentAdvisorChoice?: OneTimePaymentAdvisorChoice;
}
export const getCorrectCoverageAmountCapital = (
  isAdvisor: boolean,
  changeType: AdviceProductChangeType,
  changeValue: number | boolean,
  formValues: AdvicePriceFormValues,
  oneTimePaymentValue: number,
): ReturnData => {
  if (isAdvisor) {
    switch (changeType) {
      case AdviceProductChangeType.ADVISOR_ADVICED_ONE_TIME_PAYMENT:
        return {
          finalDisabilityOneTimePaymentSet: true,
          coverageAmountCapital: oneTimePaymentValue,
          disabilityOneTimePaymentAdvisorChoice:
            OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT,
        };
      case AdviceProductChangeType.ADVISOR_CUSTOM_ONE_TIME_PAYMENT:
        return {
          finalDisabilityOneTimePaymentSet: true,
          coverageAmountCapital: changeValue as number,
          disabilityOneTimePaymentAdvisorChoice:
            OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT,
        };
      case AdviceProductChangeType.ADVISOR_NO_ONE_TIME_PAYMENT:
        return {
          finalDisabilityOneTimePaymentSet: false,
          coverageAmountCapital: 0,
          disabilityOneTimePaymentAdvisorChoice:
            OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT,
        };
      default:
        const advisorChoice = formValues.disabilityOneTimePaymentAdvisorChoice;
        const advisorHasNotChangedOneTimePayment = !isDefined(advisorChoice);
        const customerSelectedOneTimePayment =
          formValues.disabilityOneTimePayment;

        if (advisorHasNotChangedOneTimePayment) {
          return {
            finalDisabilityOneTimePaymentSet: customerSelectedOneTimePayment,
            coverageAmountCapital: oneTimePaymentValue,
            disabilityOneTimePaymentAdvisorChoice: undefined,
          };
        }

        return {
          finalDisabilityOneTimePaymentSet:
            advisorChoice !== OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT,
          coverageAmountCapital:
            advisorChoice ===
            OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT
              ? formatNumberStringToNumber(
                  formValues.disabilityOneTimePaymentAdvisorValue,
                )
              : oneTimePaymentValue,
          disabilityOneTimePaymentAdvisorChoice: advisorChoice,
        };
    }
  }
  return {
    finalDisabilityOneTimePaymentSet:
      changeType === AdviceProductChangeType.ONE_TIME_PAYMENT_CHECKBOX
        ? (changeValue as boolean)
        : formValues.disabilityOneTimePayment,
    coverageAmountCapital: oneTimePaymentValue,
  };
};

export const getCorrectInsuranceTypeForDisabilityOneTimePayment = (
  isAdvisor: boolean,
  disabilityOneTimePayment: SinglePayment | PaymentConfiguration,
): AdviceInsuranceType => {
  if (isAdvisor && isCustomerOver50AndCanNotHaveOTP(disabilityOneTimePayment)) {
    return DisabilityInsuranceType.DISABILITY_FLEX_NO_SINGLE_PAYMENT_ADVISOR;
  }
  if (!isAdvisor && !isOneTimePaymentAllowed(disabilityOneTimePayment)) {
    return DisabilityInsuranceType.DISABILITY_FLEX_NO_SINGLE_PAYMENT;
  }
  return DisabilityInsuranceType.DISABILITY_FLEX;
};

export const isCustomerOver50AndCanNotHaveOTP = (
  singlePayment: SinglePayment | PaymentConfiguration,
): boolean => {
  if ("recommendationConfiguration" in singlePayment) {
    const { recommendation, causes } =
      singlePayment.recommendationConfiguration;
    return (
      recommendation === PurchaseRecommendation.NOT_ALLOWED &&
      isDefined(causes) &&
      causes.includes(RecommendationReason.AGE)
    );
  }
  const { restrictions } = singlePayment;
  return (
    restrictions.purchaseRestriction === PurchaseRestriction.NOT_ALLOWED &&
    restrictions.restrictionReasons.includes(PurchaseRestrictionReason.AGE)
  );
};

export interface AdviceRestrictions {
  minPayment: number;
  maxCustomer: number;
  maxAdvisor: number;
  maxAuthorizedAdvisor: number;
  maxProductManager: number;
}

export const getAdviceRestrictions = (
  insuranceAdvice: InsuranceAdvice,
  getOTPRestrictions = false,
): AdviceRestrictions => {
  const { restrictions } = getOTPRestrictions
    ? insuranceAdvice.singlePayment
    : getCorrectPayment(insuranceAdvice);
  if (isDefined(restrictions)) {
    const { minPayment, maxPayouts } = restrictions;
    return {
      minPayment: minPayment,
      maxCustomer: maxPayouts[UserAuthorizationLevel.CUSTOMER],
      maxAdvisor: maxPayouts[UserAuthorizationLevel.ADVISOR],
      maxAuthorizedAdvisor:
        maxPayouts[UserAuthorizationLevel.AUTHORIZED_ADVISOR],
      maxProductManager: maxPayouts[UserAuthorizationLevel.PRODUCT_MANAGER],
    };
  }
  return {
    minPayment: 0,
    maxCustomer: 0,
    maxAdvisor: 0,
    maxAuthorizedAdvisor: 0,
    maxProductManager: 0,
  };
};
